import { memo, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { EventAccordionItemProps } from 'src/components/Accordions';
import { AccountDisabledNotification } from 'src/components/common/AccountDisabledNotification';
import { AccountInactiveNotification } from 'src/components/common/AccountInactiveNotification';
import { EventListing } from 'src/components/Events/EventListing';
import { PerformerVenueList } from 'src/components/Events/PerformerVenueList';
import { ListingFilterBar } from 'src/components/Listings/ListingFilterBar';
import { ListingSearchBox } from 'src/components/Listings/ListingFilterBar/ListingSearchBox';
import { MainFilterBar } from 'src/components/MainFilterBar';
import { CatalogMultiSelectionContextProvider } from 'src/contexts/CatalogMultiSelectionContext';
import { Content, useContent } from 'src/contexts/ContentContext';
import { DialogProvider } from 'src/contexts/DialogContext/DialogContext';
import { useFilterQueryContextFilterQuerySelector } from 'src/contexts/FilterQueryContext/FilterQueryContext.selectors';
import { MultiSelectionContextProvider } from 'src/contexts/MultiSelectionContext';
import { ExplorerLayout } from 'src/core/layouts';
import { Button, Stack } from 'src/core/ui';
import { useMatchMedia } from 'src/hooks/useMatchMedia';
import { useUserHasAdvancedSearchV2Feature } from 'src/hooks/useUserHasFeature';
import { useUserHasConsignorMasterUI } from 'src/hooks/useUserIsConsignorMasterUI';
import { LayoutContent } from 'src/navigations/LayoutContent';
import { createInventorySearchPageUrl } from 'src/utils/advancedSearchUtils';
import { ContentId } from 'src/utils/constants/contentId';
import {
  ActionOutboxEntityType,
  CatalogEntity,
  Event,
  EventGroupingType,
  InventoryViewMode,
  Listing,
  ListingQuery,
} from 'src/WebApiController';
import Swiper from 'swiper';

import { MainRoute } from '../MainRoute';

function InventoryEventsExplorerRaw({
  onPerformerVenueRendered,
  onEventsRendered,
}: {
  onPerformerVenueRendered?: (
    items: CatalogEntity[],
    itemType: EventGroupingType
  ) => void;
  onEventsRendered?: (items: Event[]) => void;
}) {
  const routeTitle = useContent(ContentId.Inventory);
  const [filterQuery] =
    useFilterQueryContextFilterQuerySelector<ListingQuery>();

  const onItemsRendered = useCallback(
    (items: EventAccordionItemProps<Listing>[]) => {
      onEventsRendered?.(items.flatMap((i) => i?.event ?? []));
    },
    [onEventsRendered]
  );

  const swiperRef = useRef<Swiper>();
  const mobile = useMatchMedia('mobile');
  const isConsignorMasterView = useUserHasConsignorMasterUI();
  const navigate = useNavigate();

  const hasSearchV2Feature = useUserHasAdvancedSearchV2Feature(
    ActionOutboxEntityType.Listing
  );

  const onAdvancePurchaseSearch = useCallback(() => {
    const searchUrl = createInventorySearchPageUrl(hasSearchV2Feature);

    setTimeout(() => {
      navigate(searchUrl, {
        state: {
          returnUrl: `${location.pathname}${location.search}`,
        },
      });
    }, 10);
  }, [hasSearchV2Feature, navigate]);

  return (
    <CatalogMultiSelectionContextProvider type="listing">
      <MultiSelectionContextProvider
        isServerSidePagination={false}
        isFlattenedView={
          filterQuery.viewMode === InventoryViewMode.FlattenedView
        }
      >
        <DialogProvider>
          <LayoutContent
            mainRoute={MainRoute.Inventory}
            routeTitle={routeTitle}
            centerContent={
              <Stack gap="m" alignItems="center" justifyContent="center">
                <ListingSearchBox />
                {!isConsignorMasterView && (
                  <Button variant="text" onClick={onAdvancePurchaseSearch}>
                    <div style={{ whiteSpace: 'nowrap' }}>
                      <Content id={ContentId.AdvancedSearch} />
                    </div>
                  </Button>
                )}
              </Stack>
            }
          >
            <AccountDisabledNotification />
            <AccountInactiveNotification />
            <MainFilterBar swiperRef={swiperRef} />
            <ExplorerLayout
              swiperRef={swiperRef}
              showExpandButton
              leftPanelContent={
                <PerformerVenueList
                  entityType={ActionOutboxEntityType.Listing}
                  onItemsRendered={onPerformerVenueRendered}
                  swiperRef={swiperRef}
                />
              }
              leftPanelFlyover={
                filterQuery.viewMode === InventoryViewMode.MetricView
              }
            >
              <Stack direction="column" width="full" height="full">
                {mobile && (
                  <ListingFilterBar swiperRef={swiperRef} isSearchOnly />
                )}
                <EventListing onItemsRendered={onItemsRendered} />
              </Stack>
            </ExplorerLayout>
          </LayoutContent>
        </DialogProvider>
      </MultiSelectionContextProvider>
    </CatalogMultiSelectionContextProvider>
  );
}

export const InventoryEventsExplorer = memo(InventoryEventsExplorerRaw);
